/* eslint-disable react-refresh/only-export-components */
import { createPortal } from 'react-dom';
import { RiCloseLine, RiCheckLine, RiErrorWarningLine, RiInformationLine } from '@remixicon/react';
import { useEffect, createContext, useContext, ReactNode, useState, useCallback } from 'react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastAction {
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary' | 'danger';
}

export interface ToastProps {
  id: string;
  type: ToastType;
  title: string;
  message: string;
  position: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'top-center' | 'bottom-center';
  duration?: number;
  actions?: ToastAction[];
  onClose: (id: string) => void;
  index?: number;
}

const typeConfig = {
  success: {
    icon: RiCheckLine,
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    iconColor: 'text-green-600',
    titleColor: 'text-green-800',
  },
  error: {
    icon: RiErrorWarningLine,
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    iconColor: 'text-red-600',
    titleColor: 'text-red-800',
  },
  warning: {
    icon: RiErrorWarningLine,
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-200',
    iconColor: 'text-yellow-600',
    titleColor: 'text-yellow-800',
  },
  info: {
    icon: RiInformationLine,
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    iconColor: 'text-blue-600',
    titleColor: 'text-blue-800',
  },
};

const getPositionStyle = (position: ToastProps['position'], index: number = 0) => {
  const baseOffset = 16; // 16px
  const toastHeight = 80; // Altura aproximada de um toast
  const spacing = 4; // Espaçamento entre toasts
  
  const verticalOffset = baseOffset + (index * (toastHeight + spacing));
  
  const baseStyle = {
    position: 'fixed' as const,
    // Acima de Modal (50), BottomSheet (9999/10000) e overlays similares
    zIndex: 11000,
    maxWidth: '300px',
    width: '100%',
    margin: '0 16px',
  };
  
  switch (position) {
    case 'top-left':
      return { ...baseStyle, top: `${verticalOffset}px`, left: '16px' };
    case 'top-right':
      return { ...baseStyle, top: `${verticalOffset}px`, right: '16px' };
    case 'bottom-left':
      return { ...baseStyle, bottom: `${verticalOffset}px`, left: '16px' };
    case 'bottom-right':
      return { ...baseStyle, bottom: `${verticalOffset}px`, right: '16px' };
    case 'top-center':
      return { 
        ...baseStyle, 
        top: `${verticalOffset}px`, 
        left: '50%', 
        transform: 'translateX(-50%)' 
      };
    case 'bottom-center':
      return { 
        ...baseStyle, 
        bottom: `${verticalOffset}px`, 
        left: '50%', 
        transform: 'translateX(-50%)' 
      };
    default:
      return { ...baseStyle, top: `${verticalOffset}px`, right: '16px' };
  }
};

export function Toast({
  id,
  type,
  title,
  message,
  position,
  duration = 5000,
  actions,
  onClose,
  index = 0,
}: ToastProps) {
  const config = typeConfig[type];
  const Icon = config.icon;
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Trigger animation on mount
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 10);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        onClose(id);
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [duration, id]); // Removido onClose das dependências

  const getActionVariantClasses = (variant: ToastAction['variant'] = 'primary') => {
    switch (variant) {
      case 'primary':
        return 'bg-blue-600 text-white hover:bg-blue-700';
      case 'secondary':
        return 'bg-gray-600 text-white hover:bg-gray-700';
      case 'danger':
        return 'bg-red-600 text-white hover:bg-red-700';
      default:
        return 'bg-blue-600 text-white hover:bg-blue-700';
    }
  };

  return createPortal(
    <div
      style={getPositionStyle(position, index)}
      className={`transition-all duration-500 ease-out transform ${
        isVisible 
          ? 'translate-y-0 opacity-100' 
          : 'translate-y-4 opacity-0'
      }`}
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
    >
      <div
        className={`${config.bgColor} ${config.borderColor} border rounded-lg shadow-lg px-4 py-3 transition-all duration-300 ease-in-out`}
      >
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <Icon className={`h-5 w-5 ${config.iconColor}`} />
          </div>
          <div className="ml-3 w-0 flex-1">
            <p className={`text-xs sm:text-sm font-medium ${config.titleColor}`}>
              {title}
            </p>
            <p className="mt-1 text-xs sm:text-sm text-gray-600">
              {message}
            </p>
            {actions && actions.length > 0 && (
              <div className="mt-3 flex space-x-2">
                {actions.map((action, index) => (
                  <button
                    key={index}
                    onClick={action.onClick}
                    className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${getActionVariantClasses(action.variant)}`}
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="ml-4 flex-shrink-0 flex">
            <button
              className={`${config.iconColor} hover:opacity-75 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white focus:ring-current`}
              onClick={() => onClose(id)}
            >
              <RiCloseLine className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.getElementById('portal-root') || document.body
  );
}

// Contexto do Toast
export interface ToastData {
  id: string;
  type: ToastType;
  title: string;
  message: string;
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'top-center' | 'bottom-center';
  duration?: number;
  actions?: ToastAction[];
}

export interface ToastContextData {
  toasts: ToastData[];
  showToast: (toast: Omit<ToastData, 'id'>) => string;
  hideToast: (id: string) => void;
  hideAllToasts: () => void;
}

const ToastContext = createContext<ToastContextData>({} as ToastContextData);

export const useToast = (): ToastContextData => {
  const context = useContext(ToastContext);
  
  if (!context) {
    throw new Error('useToast deve ser usado dentro de um ToastProvider');
  }
  
  return context;
};

// Provider do Toast
interface ToastProviderProps {
  children: ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastData[]>([]);

  const showToast = (toast: Omit<ToastData, 'id'>): string => {
    const id = Math.random().toString(36).substr(2, 9);
    const newToast: ToastData = {
      ...toast,
      id,
    };

    setToasts(prev => [...prev, newToast]);
    return id;
  };

  const hideToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const hideAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  const contextValue = {
    toasts,
    showToast,
    hideToast,
    hideAllToasts,
  };

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      {toasts.map((toast, index) => (
        <Toast
          key={toast.id}
          {...toast}
          onClose={hideToast}
          position={toast.position || 'top-right'}
          index={index}
        />
      ))}
    </ToastContext.Provider>
  );
}
